import { Box, Typography, alpha, styled } from '@mui/material';
import {
  GOLD_DEEP,
  GOLD_TEXT,
} from 'src/components/careerProPitch/sheet.styles';

const Zero = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  '& .n': {
    fontSize: 48,
    lineHeight: 1,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: theme.palette.primary.main,
  },
  '& .t': {
    fontSize: 17,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
}));

const Steps = styled('ol')(({ theme }) => ({
  margin: '18px 0 4px',
  padding: 0,
  listStyle: 'none',
  '& li': {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '18px 1fr auto',
    columnGap: 12,
    paddingBottom: 16,
  },
  '& li:last-of-type': { paddingBottom: 0 },
  // The line joining one step's dot to the next.
  '& li:not(:last-of-type)::after': {
    content: '""',
    position: 'absolute',
    left: 8,
    top: 18,
    bottom: 0,
    width: 2,
    backgroundColor: alpha(GOLD_DEEP, 0.35),
  },
  '& .dot': {
    width: 18,
    height: 18,
    marginTop: 1,
    borderRadius: '50%',
    border: `3px solid ${GOLD_DEEP}`,
    backgroundColor: theme.palette.background.paper,
  },
  '& li:first-of-type .dot': { backgroundColor: GOLD_DEEP },
  '& .when': {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.06em',
    color: GOLD_TEXT,
    textTransform: 'uppercase',
  },
  '& .what': {
    marginTop: 2,
    fontSize: 14.5,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  '& .why': {
    marginTop: 2,
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  '& .amount': {
    alignSelf: 'start',
    fontSize: 14,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    '& s': {
      marginRight: 5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },
}));

/**
 * A Pro mandate with a free first period, as the buyer meets it: nothing
 * today, then the price and when it starts, then that they can stop it -- a
 * timeline rather than an invoice, so the free period the offer promised is
 * the first thing they read here too.
 */
const FreeMonthTimeline = ({
  freeCycles,
  freeDays,
  price,
  listPrice,
  firstChargeLabel,
  monthsCovered,
}: {
  freeCycles: number;
  /** The intro offer's free days, in place of `freeCycles` months. */
  freeDays?: number | null;
  price: number;
  listPrice: number;
  firstChargeLabel: string;
  /** Months of cover the mandate pays for, plus any free months. */
  monthsCovered: number;
}) => {
  const span = freeCycles === 1 ? 'month' : `${freeCycles} months`;
  return (
    <Box>
      <Zero>
        <span className="n">₹0</span>
        <span className="t">today</span>
      </Zero>
      <Steps>
        <li>
          <span className="dot" />
          <div>
            <div className="when">Today</div>
            <div className="what">
              {freeDays
                ? `Your ${freeDays} free days of Pro start`
                : `Your free ${span} of Pro starts`}
            </div>
            <div className="why">All of Pro, nothing charged</div>
          </div>
          <span className="amount">₹0</span>
        </li>
        <li>
          <span className="dot" />
          <div>
            <div className="when">{firstChargeLabel}</div>
            <div className="what">Pro renews monthly</div>
            <div className="why">
              {listPrice > price ? 'Your price, locked' : 'Monthly'}
              {monthsCovered > 0 ? ` for up to ${monthsCovered} months` : ''}
            </div>
          </div>
          <span className="amount">
            {listPrice > price && <s>₹{listPrice}</s>}₹{price}/mo
          </span>
        </li>
        <li>
          <span className="dot" />
          <div>
            <div className="when">Anytime</div>
            <div className="what">Cancel in a tap</div>
            <div className="why">
              Cancel before {firstChargeLabel} and you won&apos;t be charged
            </div>
          </div>
        </li>
      </Steps>
    </Box>
  );
};

/** The deal in one line, right above the button that agrees to it. */
export const FreeTrialTermsLine = ({
  until,
  price,
}: {
  until: string;
  price: number;
}) => (
  <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
    Free till {until}, then ₹{price}/month ·{' '}
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      cancel anytime
    </Box>
  </Typography>
);

export const FreeMonthNextStep = () => (
  <Typography
    sx={{ fontSize: 12, textAlign: 'center', color: 'text.secondary' }}
  >
    Next: approve the monthly autopay in Razorpay. Nothing is charged today; a
    small bank verification charge is refunded automatically.
  </Typography>
);

export default FreeMonthTimeline;
